import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { PageHero } from "../components/PageShell";
import { Figure } from "../components/Figure";
import { usePublishedNews } from "../lib/publishedNews";
import { ROUTES } from "../lib/routes";

const ALL = "All";
const PER_PAGE = 8;

/** webadmin's published news, as published (see publishedNews.ts). */
export default function News() {
  const { posts, loading } = usePublishedNews();
  const [filter, setFilter] = useState(ALL);
  const [page, setPage] = useState(1);

  // webadmin's own categories, as published, in the order its posts first use them.
  const cats = useMemo(() => [ALL, ...new Set(posts.map((p) => p.category).filter(Boolean))], [posts]);
  const filtered = filter === ALL ? posts : posts.filter((p) => p.category === filter);

  // Every post is a list row, featured or not: no lead card above the list.
  const totalPages = Math.max(1, Math.ceil(filtered.length / PER_PAGE));
  const visible = filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE);

  return (
    <>
      <PageHero
        variant="hero-editorial hero-compact"
        fullWidthTitle
        eyebrow="News & Events · Archive"
        title="Stories from across the BIET campus."
        lead="News from the institute and its departments, as BIET publishes it. Filter by category to find what you need."
        crumbs={[{ label: "News & Events" }]}
        image={null}
      />

      <section className="section">
        <div className="container">
          {posts.length > 0 && (
            <div className="news-filter-bar">
              {cats.map((c) => (
                <button
                  key={c}
                  className={"news-chip" + (filter === c ? " active" : "")}
                  onClick={() => {
                    setFilter(c);
                    setPage(1);
                  }}
                >
                  {c}
                </button>
              ))}
              <span className="news-count">
                {filtered.length} {filtered.length === 1 ? "STORY" : "STORIES"}
              </span>
            </div>
          )}

          <div className="news-list">
            {visible.map((n) => (
              <article key={n.slug} className={"news-row reveal" + (n.cover ? "" : " no-image")}>
                <div className="ndate">
                  <b>{n.date.day}</b>
                  {n.date.month} {n.date.year}
                </div>
                <div className="ncat">{n.category}</div>
                <div>
                  <h3>{n.title}</h3>
                  <p className="teaser">{n.summary || n.content}</p>
                  <Link to={`${ROUTES.news}/${n.slug}`} className="read-more">
                    Read more →
                  </Link>
                </div>
                {n.cover && <Figure media={n.cover} fallbackLabel={n.title} aspect="4/3" className="nimage" sizes="240px" />}
              </article>
            ))}
            {!loading && posts.length === 0 && <div className="faculty-empty">No news has been published yet.</div>}
            {posts.length > 0 && visible.length === 0 && (
              <div className="faculty-empty">No stories under this category yet.</div>
            )}
          </div>

          {totalPages > 1 && (
            <div className="pager">
              <button onClick={() => setPage(Math.max(1, page - 1))} disabled={page === 1}>
                ‹
              </button>
              {Array.from({ length: totalPages }).map((_, i) => (
                <button key={i} className={page === i + 1 ? "current" : ""} onClick={() => setPage(i + 1)}>
                  {i + 1}
                </button>
              ))}
              <button onClick={() => setPage(Math.min(totalPages, page + 1))} disabled={page === totalPages}>
                ›
              </button>
            </div>
          )}
        </div>
      </section>
    </>
  );
}
